import React, { useState, useRef, useEffect } from 'react';
import { Box, TextField, IconButton, Typography, Avatar, Paper } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useGetCompanyChatQuery, useSendCompanyChatMessageMutation } from '../../../api/dieter/companyChat/hooks';
import { useUser } from '../../../context/UserContext';

interface CompanyChatProps {
  chatId: string;
}

export function CompanyChat({ chatId }: CompanyChatProps) {
  const { user } = useUser();
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data, loading, error, refetch } = useGetCompanyChatQuery(chatId, {
    skip: !chatId,
  });
  const [sendMessage] = useSendCompanyChatMessageMutation();

  const chat = data?.getCompanyChat;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat?.messages]);

  // Poll for new messages every 2 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, 2000);

    return () => clearInterval(interval);
  }, [refetch]);

  const handleSend = async () => {
    if (!message.trim()) return;

    const content = message;
    setMessage('');

    await sendMessage({
      variables: {
        chatId,
        content,
      },
    });

    // Refetch immediately after sending
    await refetch();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (loading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">Error loading chat</Typography>;
  if (!chat) return <Typography>Chat not found</Typography>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Typography variant="h6">{chat.title}</Typography>
        <Typography variant="caption" color="text.secondary">
          {chat.company.name}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          minHeight: 0,
        }}
      >
        {chat.messages.map((msg: any) => {
          const isOwnMessage = msg.senderId === user?.id;
          return (
            <Box
              key={msg.id}
              sx={{
                display: 'flex',
                justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
                gap: 1,
              }}
            >
              {!isOwnMessage && (
                <Avatar sx={{ width: 32, height: 32 }}>
                  {msg.sender?.firstName?.[0]}
                  {msg.sender?.lastName?.[0]}
                </Avatar>
              )}
              <Paper
                sx={{
                  p: 1.5,
                  maxWidth: '70%',
                  bgcolor: isOwnMessage ? 'primary.light' : 'grey.100',
                }}
              >
                {!isOwnMessage && (
                  <Typography variant="caption" fontWeight="bold" display="block">
                    {msg.sender?.firstName} {msg.sender?.lastName}
                    {msg.sender?.role && ` (${msg.sender.role})`}
                  </Typography>
                )}
                <Typography variant="body2">{msg.content}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(msg.createdAt).toLocaleTimeString()}
                </Typography>
              </Paper>
              {isOwnMessage && (
                <Avatar sx={{ width: 32, height: 32 }}>
                  {user?.firstName?.[0]}
                  {user?.lastName?.[0]}
                </Avatar>
              )}
            </Box>
          );
        })}
        <div ref={messagesEndRef} />
      </Box>

      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          gap: 1,
          flexShrink: 0,
        }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder="Type a message..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={handleKeyPress}
        />
        <IconButton color="primary" onClick={handleSend} disabled={!message.trim()}>
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
